import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Grid, Typography, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TextField
} from "@mui/material";
import { Movement } from "types";
import useCrud from "hooks/useCrud";
import { INVENTORY_ADJUSTMENTS_URL } from "constants/urls";
import { useToast } from "hooks/useToast";
import { round, toNumber } from "helpers";
import { MovementTypes } from "constants/index";

type InventoryAdjustmentModalProps = {
  open: boolean;
  onClose: () => void;
  movement: Movement;
};

type ArticleRow = {
  index: number;
  article_locations_id?: number;
  code: string;
  description: string;
  oldAmount: number;
  newAmount: number;
  currentStock: number;
  diff: number;
  resultingStock: number;
  error?: string | null;
};

export default function InventoryAdjustmentModal({
  open,
  onClose,
  movement,
}: InventoryAdjustmentModalProps) {

  const [rows, setRows] = useState<ArticleRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createData } = useCrud(INVENTORY_ADJUSTMENTS_URL, { disableAutoFetch: true });
  const { showSnackBar } = useToast();

  useEffect(() => {
    if (!open) return;
    const mapped = (movement.articles ?? []).map((a, idx) => {
      const oldAmount = Number(a.amount ?? 0);
      const currentStock = Number(a.article_locations?.stock ?? 0);
      return {
        index: idx,
        article_locations_id: a.article_locations_id,
        code: a.code ?? "-",
        description: a.description ?? "-",
        oldAmount,
        newAmount: oldAmount,
        currentStock,
        diff: 0,
        resultingStock: currentStock,
        error: null,
      } as ArticleRow;
    });
    setRows(mapped);
  }, [open, movement]);

  const updateRowAmount = (index: number, newAmountInput: string) => {
    setRows(prev => {
        const copy = [...prev];
        const row = { ...copy[index] };

        const newAmount = toNumber(newAmountInput);
        const diff = newAmount - row.oldAmount;
        const sign =
        movement.movement_type_id === MovementTypes.Discharge ? -1 : 1;
        const resultingStock = row.currentStock + diff * sign;

        let error: string | null = null;
        if (newAmount < 0) error = "La cantidad no puede ser negativa.";
        if (resultingStock < 0) error = "El stock resultante no puede ser negativo.";

        row.newAmount = round(newAmount, 3);
        row.diff = round(diff * sign, 3);
        row.resultingStock = round(resultingStock, 3);
        row.error = error;

        copy[index] = row;
        return copy;
    });
  };


  const hasChanges = useMemo(
    () => rows.some(r => r.newAmount !== r.oldAmount),
    [rows]
  );

  const hasErrors = useMemo(
    () => rows.some(r => !!r.error),
    [rows]
  );

  const handleConfirm = async () => {
    try {
        setIsSubmitting(true);

        const payload = {
        movement_type_id: movement.movement_type_id,
        movement_id: movement.id,
        adjustments: rows
            .filter(r => r.newAmount !== r.oldAmount)
            .map(r => ({
            article_location_id: r.article_locations_id,
            prev_amount: r.oldAmount,
            new_amount: r.newAmount,
            diff: round(r.newAmount - r.oldAmount, 3),
            })),
        };

        if (
        movement.movement_type_id !== MovementTypes.Income &&
        movement.movement_type_id !== MovementTypes.Discharge
        ) {
        showSnackBar(
            "Solo se pueden ajustar movimientos de tipo Ingreso o Egreso.",
            "error"
        );
        setIsSubmitting(false);
        return;
        }

        const response = await createData(payload, true, true);

        if (response?.status === 200 || response?.status === 201) {
        showSnackBar("Ajuste de inventario aplicado correctamente.", "success");
        onClose();
        }
    } catch (err) {
        console.error(err);
        showSnackBar("Error al aplicar el ajuste de inventario.", "error");
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Ajuste de inventario</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TableContainer component={Paper} elevation={4} sx={{ p: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Cantidad actual</TableCell>
                    <TableCell align="right">Nueva cantidad</TableCell>
                    <TableCell align="right">Dif.</TableCell>
                    <TableCell align="right">Stock actual</TableCell>
                    <TableCell align="right">Stock resultante</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{r.code}</TableCell>
                      <TableCell>{r.description}</TableCell>
                      <TableCell align="right">{r.oldAmount}</TableCell>
                      <TableCell align="right" sx={{ minWidth: 160 }}>
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ step: "any" }}
                          value={r.newAmount}
                          onChange={(e) => updateRowAmount(i, e.target.value)}
                          error={!!r.error}
                          helperText={r.error ?? ""}
                        />
                      </TableCell>
                      <TableCell align="right">{r.diff}</TableCell>
                      <TableCell align="right">{r.currentStock}</TableCell>
                      <TableCell align="right">{r.resultingStock}</TableCell>
                      <TableCell>
                        {r.newAmount === r.oldAmount
                          ? "Sin cambios"
                          : (r.error ? "Inválido" : "Listo")}
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No hay artículos para ajustar.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!hasChanges || hasErrors || isSubmitting}
        >
          {isSubmitting ? "Aplicando..." : "Aplicar ajustes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
